import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { Map as A1, CheckSquare as A2, Calendar as A3, Flag as A4, Bell as A5, TrendingUp as A6 } from 'lucide-react';

import { BuiltFromStrip } from '../components/LifeDesignIntelligence';
import SituationStrip from '../components/SituationStrip';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  Rocket, Loader2, LayoutGrid, CalendarRange, Bot, History, RefreshCw, Plus, FileDown,
  Flag, MessageSquare, Send,
} from 'lucide-react';
import HorizonWizard from '../components/plan/HorizonWizard';
import DraftReview from '../components/plan/DraftReview';
import PlanBoard from '../components/plan/PlanBoard';
import PlanTimeline from '../components/plan/PlanTimeline';
import CoachPanel from '../components/plan/CoachPanel';
import TaskDrawer from '../components/plan/TaskDrawer';
import ChangeSyncModal from '../components/plan/ChangeSyncModal';
import { API, fmtDate } from '../lib/planConstants';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const TABS = [
  { id: 'timeline', label: 'Roadmap', icon: CalendarRange },
  { id: 'board', label: 'Task Board', icon: LayoutGrid },
  { id: 'coach', label: 'AI Coach', icon: Bot },
  { id: 'history', label: 'History', icon: History },
];

export default function MyPlanPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const authHeader = getAuthHeader ? getAuthHeader() : {};

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [options, setOptions] = useState([]);
  const [gradeInfo, setGradeInfo] = useState(null);
  const [tab, setTab] = useState('timeline');
  const [openTask, setOpenTask] = useState(null);
  const [coachFocus, setCoachFocus] = useState(null);
  const [showChange, setShowChange] = useState(false);
  const [coach, setCoach] = useState(null);
  const [history, setHistory] = useState(null);
  const [feedback, setFeedback] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/futurepath-plan`, authHeader);
      setData(r.data);
      if (r.data.status === 'active') {
        const [c, h] = await Promise.all([
          axios.get(`${API}/futurepath-plan/coach/next-actions`, authHeader).catch(() => null),
          axios.get(`${API}/futurepath-plan/versions`, authHeader).catch(() => null),
        ]);
        setCoach(c?.data || null);
        setHistory(h?.data || null);
      }
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    axios.get(`${API}/futurepath-plan/horizon-options`, authHeader)
      .then((r) => { setOptions(r.data.options || []); setGradeInfo(r.data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while the plan is generating
  useEffect(() => {
    if (data?.status !== 'generating') return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);

  const changeStatus = async (task, status) => {
    try {
      const r = await axios.patch(`${API}/futurepath-plan/tasks/${task.id}`, { status }, authHeader);
      if (r.data.milestone_completed) {
        celebrate(`Milestone complete: ${r.data.milestone_completed}${r.data.badge_earned ? ` · Badge earned: ${r.data.badge_earned}` : ''}`);
      }
      if ((r.data.unblocked || []).length) toast.info(`Unlocked: ${r.data.unblocked.join(', ')}`);
      window.dispatchEvent(new Event('goals-updated'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not move that task.');
    }
  };

  const sendFeedback = async () => {
    if (!feedback.trim()) return;
    try {
      await axios.post(`${API}/futurepath-plan/feedback`,
        { student_id: data.student?.id, text: feedback }, authHeader);
      toast.success('Feedback sent to your student');
      setFeedback('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send feedback.');
    }
  };

  if (loading) return <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="my_plan" /></div><div className="flex justify-center py-20"><LoadingSpinner /></div></AppLayout>;

  const plan = data?.plan;
  const status = data?.status || 'none';
  const tasks = (plan?.tasks || []).filter((t) => !t.archived);
  const isParentView = data?.access === 'parent' || data?.access === 'admin';

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="roadmap-arrival"
        eyebrow="My Roadmap"
        title="Everything you decided, in order."
        subtitle="One list, dated and sequenced, of every step between today and the target, with what is due, what is done, and what only becomes possible once something else is finished."
        accent="#22d3ee"
        second="#a78bfa"
        ctaLabel="Open my roadmap"
        narration={"This is My Roadmap. Every decision you have made turns into a dated step here, in the order that makes them possible. You will see what is due this week, what is overdue, and what is waiting on something else. Finish something and it moves, and the plan updates around it instead of going stale."}
        facts={[
          { icon: A3, head: "Dated and sequenced", sub: "Order matters, and we keep it" },
          { icon: A2, head: "Small enough to finish", sub: "Actions, not ambitions" },
          { icon: A6, head: "Moves with you", sub: "Finish one and the rest re-plans" }
        ]}
        modules={[
          { icon: A2, label: "This week", line: "The short list that actually matters now", hue: "#22d3ee" },
          { icon: A3, label: "Deadlines", line: "Applications, aid, tests and enrolment", hue: "#fbbf24" },
          { icon: A4, label: "Milestones", line: "The moments worth marking", hue: "#a78bfa" },
          { icon: A1, label: "The full path", line: "Every step from today to the target", hue: "#34d399" },
          { icon: A5, label: "Reminders", line: "Told once, in time to act", hue: "#f472b6" },
          { icon: A6, label: "Progress", line: "What you have finished, kept in view", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <BuiltFromStrip />
      <div className="max-w-6xl mx-auto p-2 sm:p-4 space-y-6" data-testid="my-plan-page">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold"
            style={{ textShadow: '0 0 14px rgba(23,204,244,0.6)' }}>Launch · Execution Plan</p>
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-white via-cyan-100 to-cyan-300 bg-clip-text text-transparent"
            style={{ filter: 'drop-shadow(0 0 22px rgba(23,204,244,0.28))' }}>
            {isParentView ? `${data.student?.name}'s plan` : 'My FuturePath Plan'}
          </h1>
          <p className="text-slate-300 text-sm mt-1">
            One living plan from where you are now to the destination you chose, milestones, tasks, deadlines and an AI coach.
          </p>
        </div>

        <SituationStrip page="roadmap" />

        {status === 'none' && !isParentView && (
          <HorizonWizard options={options} gradeInfo={gradeInfo} authHeader={authHeader}
            onGenerating={() => { setData({ ...(data || {}), status: 'generating' }); load(); }} />
        )}

        {status === 'none' && isParentView && (
          <div className="glass-card p-8 text-center text-slate-300" data-testid="parent-no-plan">
            {data.student?.name} hasn't built their execution plan yet. They can start it from Launch Mode.
          </div>
        )}

        {status === 'generating' && (
          <div className="glass-card p-10 text-center" data-testid="plan-generating">
            <Loader2 className="w-8 h-8 text-cyan-300 animate-spin mx-auto" />
            <h2 className="text-lg md:text-lg font-semibold text-white mt-4">Building your plan…</h2>
            <p className="text-sm text-slate-300 mt-1">
              Mapping phases, milestones, academic requirements and tasks all the way to your endpoint.
              This usually takes 2-5 minutes for a multi-year plan, you can leave this page and come back.
            </p>
          </div>
        )}

        {status === 'draft' && !isParentView && (
          <DraftReview plan={plan} authHeader={authHeader} onActivated={load}
            onDiscarded={() => { setData({ plan: null, status: 'none', access: 'self' }); }} />
        )}

        {status === 'active' && plan && (
          <>
            <div className="glass-card p-6" data-testid="plan-header">
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[240px]">
                  <h2 className="font-outfit text-xl font-bold text-white">{plan.title}</h2>
                  <p className="text-sm text-slate-300 mt-1">{plan.goal}</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-slate-300">
                    <span className="inline-flex items-center gap-1.5"><Flag className="w-3.5 h-3.5 text-cyan-300" />
                      {plan.horizon?.label} · {fmtDate(plan.horizon?.target_date)}</span>
                    <span>{(plan.phases || []).length} phases</span>
                    <span>{data.progress?.milestones_completed}/{data.progress?.milestones_total} milestones</span>
                    <span>v{plan.version}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-outfit text-3xl font-bold text-cyan-300">{data.progress?.percent}%</p>
                  <p className="text-xs text-slate-300">{data.progress?.tasks_completed}/{data.progress?.tasks_total} tasks done</p>
                  {data.progress?.overdue > 0 && (
                    <p className="text-xs text-rose-300 mt-1">{data.progress.overdue} overdue</p>
                  )}
                </div>
              </div>
              <div className="h-2 rounded-full bg-slate-900 overflow-hidden mt-4">
                <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
                  initial={{ width: 0 }} animate={{ width: `${data.progress?.percent || 0}%` }} transition={{ duration: 1 }} />
              </div>
              {!isParentView && (
                <div className="flex flex-wrap gap-3 mt-4">
                  <button onClick={() => setShowChange(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/40 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/10 transition-colors duration-200"
                    data-testid="update-plan-btn">
                    <RefreshCw className="w-3.5 h-3.5" /> Something changed, update my plan
                  </button>
                  <Link to="/app/goals" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 text-xs font-semibold text-slate-300 hover:border-cyan-500/40"
                    data-testid="open-goals-link">
                    <Plus className="w-3.5 h-3.5" /> Goals board
                  </Link>
                  <Link to="/app/report" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 text-xs font-semibold text-slate-300 hover:border-cyan-500/40"
                    data-testid="open-report-link">
                    <FileDown className="w-3.5 h-3.5" /> FuturePath Report
                  </Link>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} data-testid={`plan-tab-${t.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ${
                      tab === t.id ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-400/40'
                        : 'border border-slate-700 text-slate-300 hover:text-slate-200'}`}>
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>

            {tab === 'timeline' && <PlanTimeline plan={plan} tasks={tasks} onOpenTask={setOpenTask} />}

            {tab === 'board' && (
              <PlanBoard tasks={tasks} onOpenTask={setOpenTask}
                onStatusChange={(task, status) => (isParentView ? toast.error('Only the student can move tasks.') : changeStatus(task, status))} />
            )}

            {tab === 'coach' && (
              isParentView ? (
                <div className="glass-card p-6" data-testid="parent-feedback-card">
                  <p className="text-sm font-semibold text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-cyan-300" /> Leave feedback on this plan
                  </p>
                  <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={4}
                    className="w-full mt-3 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder="What you're proud of, what you'd like them to prioritise, what you can help with…"
                    data-testid="parent-feedback-input" />
                  <button onClick={sendFeedback} className="btn-primary mt-3 px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2"
                    data-testid="send-feedback-btn"><Send className="w-3.5 h-3.5" /> Send feedback</button>
                </div>
              ) : (
                <CoachPanel signals={coach?.signals} progress={data.progress} checkinDue={coach?.checkin_due}
                  authHeader={authHeader} onOpenTask={setOpenTask} onRefresh={load}
                  focusTask={coachFocus} clearFocus={() => setCoachFocus(null)} />
              )
            )}

            {tab === 'history' && (
              <div className="space-y-4" data-testid="plan-history">
                <div className="glass-card p-5">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Change log</h3>
                  <div className="mt-3 space-y-3">
                    {(history?.change_log || []).map((c, i) => (
                      <div key={i} className="border-l-2 border-cyan-500/40 pl-3">
                        <p className="text-xs text-slate-300">{fmtDate(c.at)} · {c.type}</p>
                        <p className="text-sm text-white">{c.summary || 'Plan updated'}</p>
                        {c.why && <p className="text-xs text-slate-300 mt-0.5">{c.why}</p>}
                        {(c.tasks_added || c.tasks_archived || c.dates_changed) && (
                          <p className="text-[11px] text-slate-300 mt-1">
                            +{c.tasks_added || 0} added · {c.tasks_archived || 0} archived · {c.dates_changed || 0} re-dated
                          </p>
                        )}
                      </div>
                    ))}
                    {!(history?.change_log || []).length && <p className="text-sm text-slate-300">No changes yet.</p>}
                  </div>
                </div>
                <div className="glass-card p-5">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Saved plan versions</h3>
                  <div className="mt-3 space-y-2">
                    {(history?.versions || []).map((v) => (
                      <div key={v.id} className="flex items-center gap-3 text-sm">
                        <span className="text-cyan-300 font-mono text-xs">v{v.version}</span>
                        <span className="text-slate-300 flex-1">{v.reason}</span>
                        <span className="text-xs text-slate-300">{fmtDate(v.created_at)}</span>
                      </div>
                    ))}
                    {!(history?.versions || []).length && (
                      <p className="text-sm text-slate-300">Your current plan is version {history?.current_version || 1}. Earlier versions appear here after an update.</p>
                    )}
                  </div>
                </div>
                {((plan.parent_feedback || []).length > 0 || (plan.counselor_feedback || []).length > 0) && (
                  <div className="glass-card p-5" data-testid="plan-feedback">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Parent & counselor feedback</h3>
                    <div className="mt-3 space-y-2">
                      {[...(plan.parent_feedback || []), ...(plan.counselor_feedback || [])].map((f) => (
                        <div key={f.id} className="text-sm text-slate-300">
                          <span className="text-white font-medium">{f.author_name}</span>
                          <span className="text-xs text-slate-300"> · {f.role} · {fmtDate(f.created_at)}</span>
                          <p className="text-slate-300">{f.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {status === 'error' && (
          <div className="glass-card p-8 text-center" data-testid="plan-error">
            <p className="text-white">We hit a snag building your plan.</p>
            <button onClick={async () => { await axios.delete(`${API}/futurepath-plan/draft`, authHeader); load(); }}
              className="btn-primary mt-4 px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="plan-retry-btn">
              <Rocket className="w-4 h-4 inline mr-2" />Start again
            </button>
          </div>
        )}
      </div>

      {openTask && (
        <TaskDrawer task={openTask} allTasks={tasks} authHeader={authHeader}
          onClose={() => setOpenTask(null)}
          onChanged={() => { load(); setOpenTask(null); }}
          onAskCoach={(t) => { setOpenTask(null); setTab('coach'); setCoachFocus(t); }} />
      )}

      {showChange && plan && (
        <ChangeSyncModal plan={plan} horizonOptions={options} authHeader={authHeader}
          onClose={() => setShowChange(false)}
          onApplied={() => { setShowChange(false); load(); }} />
      )}
    </AppLayout>
  );
}
